import React, { createContext, useCallback, useContext, useState } from 'react'
import { useDispatch } from 'react-redux'
import { cartActions } from '../store/modules/cart/actions'
import { Battery } from '../shared/types/battery'

interface PendingAdd {
  product: Battery
  quantity: number
  showToast: boolean
}

interface ScrapPreferenceContextValue {
  /** Adiciona ao carrinho; se preferência de sucata não definida, abre o modal. */
  addItemWithScrapModal: (product: Battery, quantity?: number, showToast?: boolean) => void
}

const ScrapPreferenceContext = createContext<ScrapPreferenceContextValue | null>(null)

export function useScrapPreferenceModal() {
  const ctx = useContext(ScrapPreferenceContext)
  return ctx
}

interface ScrapPreferenceProviderProps {
  children: React.ReactNode
  openCart: () => void
}

export const ScrapPreferenceProvider: React.FC<ScrapPreferenceProviderProps> = ({
  children,
  openCart
}) => {
  const dispatch = useDispatch()
  const [modalOpen, setModalOpen] = useState(false)
  const [step, setStep] = useState<1 | 2>(1)
  const [pendingAdd, setPendingAdd] = useState<PendingAdd | null>(null)

  const addItemWithScrapModal = useCallback(
    (product: Battery, quantity: number = 1, showToast: boolean = true) => {
      const allowsScrap = product.allowsScrapReturn === true && (product as { scrapReturnEnabled?: boolean | null }).scrapReturnEnabled !== false
      if (!allowsScrap) {
        dispatch(cartActions.addItem({ product, quantity, showToast, willReturnScrap: false }))
        openCart()
        return
      }
      // Sempre exibir o modal para produtos com base de troca (catálogo/home)
      setPendingAdd({ product, quantity, showToast })
      setStep(1)
      setModalOpen(true)
    },
    [dispatch, openCart]
  )

  const handleConfirmCampinas = useCallback((isCampinas: boolean) => {
    if (!isCampinas) {
      dispatch(cartActions.setScrapPreference(false))
      if (pendingAdd) {
        dispatch(cartActions.addItem({
          product: pendingAdd.product,
          quantity: pendingAdd.quantity,
          showToast: pendingAdd.showToast,
          willReturnScrap: false
        }))
        openCart()
      }
      setPendingAdd(null)
      setModalOpen(false)
      setStep(1)
      return
    }
    setStep(2)
  }, [dispatch, openCart, pendingAdd])

  const handleConfirmScrap = useCallback(
    (willReturnScrap: boolean) => {
      dispatch(cartActions.setScrapPreference(willReturnScrap))
      if (pendingAdd) {
        dispatch(cartActions.addItem({
          product: pendingAdd.product,
          quantity: pendingAdd.quantity,
          showToast: pendingAdd.showToast,
          willReturnScrap
        }))
        openCart()
      }
      setPendingAdd(null)
      setModalOpen(false)
      setStep(1)
    },
    [dispatch, openCart, pendingAdd]
  )

  const handleClose = useCallback(() => {
    setModalOpen(false)
    setPendingAdd(null)
    setStep(1)
  }, [])

  return (
    <ScrapPreferenceContext.Provider value={{ addItemWithScrapModal }}>
      {children}
      {modalOpen && (
        <ScrapPreferenceModal
          step={step}
          onClose={handleClose}
          onConfirmCampinas={handleConfirmCampinas}
          onConfirmScrap={handleConfirmScrap}
        />
      )}
    </ScrapPreferenceContext.Provider>
  )
}

interface ScrapPreferenceModalProps {
  step: 1 | 2
  onClose: () => void
  onConfirmCampinas: (isCampinas: boolean) => void
  onConfirmScrap: (willReturnScrap: boolean) => void
}

const ScrapPreferenceModal: React.FC<ScrapPreferenceModalProps> = ({
  step,
  onClose,
  onConfirmCampinas,
  onConfirmScrap
}) => {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" role="dialog" aria-modal="true" aria-labelledby="scrap-modal-title" aria-describedby="scrap-modal-desc">
      <div className="bg-white rounded-xl shadow-xl max-w-md w-full p-6">
        {step === 1 ? (
          <>
            <h2 id="scrap-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
              Entrega em Campinas ou região?
            </h2>
            <p id="scrap-modal-desc" className="text-gray-600 mb-6">
              Este produto pode ser vendido na <strong>base de troca</strong> em Campinas e região (CEP 13xxx). Você vai receber a entrega ou retirar na loja por aí?
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => onConfirmCampinas(true)}
                className="flex-1 py-3 px-4 rounded-lg font-medium bg-blue-600 text-white hover:bg-blue-700 transition-colors"
              >
                Sim, Campinas ou região
              </button>
              <button
                type="button"
                onClick={() => onConfirmCampinas(false)}
                className="flex-1 py-3 px-4 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Não, é em outra região
              </button>
            </div>
          </>
        ) : (
          <>
            <h2 id="scrap-modal-title" className="text-lg font-semibold text-gray-900 mb-2">
              Você tem a bateria usada para trocar?
            </h2>
            <p id="scrap-modal-desc" className="text-gray-600 mb-6">
              Na entrega ou na retirada você pode devolver a bateria usada (sucata) e levar pelo preço normal. Se não tiver a bateria para trocar, aplicamos um acréscimo de <strong>R$ 5,50 por kg</strong> no produto.
            </p>
            <div className="flex gap-3">
              <button
                type="button"
                onClick={() => onConfirmScrap(true)}
                className="flex-1 py-3 px-4 rounded-lg font-medium bg-green-600 text-white hover:bg-green-700 transition-colors"
              >
                Sim, vou levar a usada
              </button>
              <button
                type="button"
                onClick={() => onConfirmScrap(false)}
                className="flex-1 py-3 px-4 rounded-lg font-medium border border-gray-300 text-gray-700 hover:bg-gray-50 transition-colors"
              >
                Não tenho a bateria usada
              </button>
            </div>
          </>
        )}

        <button
          type="button"
          onClick={onClose}
          className="mt-4 w-full py-2 text-sm text-gray-500 hover:text-gray-700"
        >
          Cancelar
        </button>
      </div>
    </div>
  )
}
